import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { quantum } from 'ldrs';
import { useAuth } from '../context/AuthContext';
import { requestPasswordReset, startGoogleOAuth } from '../services/auth';

quantum.register();

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [isWakingApi, setIsWakingApi] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let timeoutId;

    const wakeApi = async () => {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const baseWithoutVersion = apiBase.replace(/\/api\/v1\/?$/, '');
      try {
        const res = await fetch(`${baseWithoutVersion}/health`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!res.ok) {
          throw new Error('Health check failed');
        }
        if (isMounted) {
          setIsWakingApi(false);
        }
      } catch (err) {
        if (isMounted) {
          timeoutId = setTimeout(wakeApi, 2500);
        }
      }
    };

    wakeApi();
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(form.email, form.password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Unable to log in right now');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setOauthLoading(true);
    try {
      const data = await startGoogleOAuth(`${window.location.origin}/oauth/callback`);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError('Unable to start Google sign-in');
      }
    } catch (err) {
      setError(err.message || 'Unable to start Google sign-in');
    } finally {
      setOauthLoading(false);
    }
  };

  const handleReset = async (event) => {
    event.preventDefault();
    setResetError('');
    setResetMessage('');
    setResetLoading(true);

    try {
      const resp = await requestPasswordReset(resetEmail || form.email);
      setResetMessage(resp?.message || 'If that email exists, a reset link has been sent.');
    } catch (err) {
      setResetError(err.message || 'Unable to send reset email right now');
    } finally {
      setResetLoading(false);
    }
  };

  if (isWakingApi) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#050815] via-[#0a0f1f] to-[#050815] px-4 text-slate-100">
        <div className="flex flex-col items-center gap-3">
          <img src="/dexter.png" alt="Dexter" className="h-20 w-20 rounded-lg object-cover" />
          <h1
            className="mt-2 text-center text-3xl font-semibold uppercase tracking-[0.12em] text-slate-50 sm:text-3xl"
            style={{ fontFamily: 'Orbitron, Rajdhani, Exo 2, ui-monospace, SFMono-Regular, Menlo, monospace' }}
          >
            MyRolodex
          </h1>
          <div className="mt-4">
            <l-quantum size="45" speed="1.75" color="#fed745" />
          </div>
          <p
            className="text-center text-sm uppercase tracking-[0.1em] text-slate-300"
            style={{ fontFamily: 'Orbitron, Rajdhani, Exo 2, ui-monospace, SFMono-Regular, Menlo, monospace' }}
          >
            The Robots are waking up...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#050815] via-[#0a0f1f] to-[#050815] px-4 text-slate-100">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0c1428]/90 p-8 shadow-[0_24px_80px_rgba(0,0,0,0.4)] backdrop-blur">
        <h1
          className="text-center text-3xl font-semibold uppercase tracking-[0.12em] text-slate-50 sm:text-3xl"
          style={{ fontFamily: 'Orbitron, Rajdhani, Exo 2, ui-monospace, SFMono-Regular, Menlo, monospace' }}
        >
          MyRolodex
        </h1>
        <img src="/dexter.png" alt="Dexter" className="mx-auto mb-4 mt-3 h-20 w-20 rounded-lg object-cover" />
        <h2 className="mt-2 text-xl font-semibold text-slate-50">Sign in to continue</h2>

        {error && <div className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">{error}</div>}
        {resetMessage && (
          <div className="mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100">
            {resetMessage}
          </div>
        )}
        {resetError && (
          <div className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">
            {resetError}
          </div>
        )}

        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-2 text-sm text-slate-300">
            <span>Email</span>
            <input
              type="email"
              name="email"
              autoComplete="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
              required
              className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-base text-slate-100 outline-none transition focus:border-sky-400/70 focus:ring-2 focus:ring-sky-400/30"
            />
          </label>

          <label className="flex flex-col gap-2 text-sm text-slate-300">
            <span>Password</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              required
              className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-base text-slate-100 outline-none transition focus:border-sky-400/70 focus:ring-2 focus:ring-sky-400/30"
            />
          </label>

          <button
            type="submit"
            className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-sky-500 px-4 py-3 text-sm font-semibold text-[#050815] shadow-lg shadow-sky-500/30 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={loading}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="mt-3">
          <button
            type="button"
            onClick={handleGoogle}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-slate-100 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={oauthLoading}
          >
            <span className="text-lg">G</span>
            {oauthLoading ? 'Redirecting…' : 'Continue with Google'}
          </button>
        </div>

        <button
          type="button"
          className="mt-3 text-sm font-semibold text-sky-300 hover:underline"
          onClick={() => setResetOpen((open) => !open)}
        >
          {resetOpen ? 'Hide password reset' : 'Forgot password?'}
        </button>

        {resetOpen && (
          <form className="mt-3 space-y-3 rounded-xl border border-white/10 bg-white/5 p-4" onSubmit={handleReset}>
            <label className="flex flex-col gap-2 text-sm text-slate-300">
              <span>Reset email</span>
              <input
                type="email"
                name="resetEmail"
                placeholder="you@example.com"
                value={resetEmail || form.email}
                onChange={(event) => setResetEmail(event.target.value)}
                required
                className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-base text-slate-100 outline-none transition focus:border-sky-400/70 focus:ring-2 focus:ring-sky-400/30"
              />
            </label>
            <button
              type="submit"
              className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold text-slate-100 transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-70"
              disabled={resetLoading}
            >
              {resetLoading ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}

        <p className="mt-4 text-sm text-slate-400">
          Need an account?{' '}
          <Link className="font-semibold text-sky-300 hover:underline" to="/signup">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
